import React, { useState } from 'react';
import { useWorkloads } from '../features/hub/workloads/hooks/useWorkloads';
import toast from 'react-hot-toast';

interface WorkloadAssignmentSelectProps {
  currentWorkloadId?: string | null;
  onAssign: (workloadId: string | null) => Promise<void>;
  disabled?: boolean;
  size?: 'sm' | 'md';
  /** When true, shows a placeholder "Set Workload" instead of reflecting currentWorkloadId */
  bulk?: boolean;
}

const PLACEHOLDER = '__placeholder__';
const CHECK = '\u2713 ';

const WorkloadAssignmentSelect: React.FC<WorkloadAssignmentSelectProps> = ({
  currentWorkloadId,
  onAssign,
  disabled = false,
  size = 'sm',
  bulk = false,
}) => {
  const { workloads } = useWorkloads();
  const [isAssigning, setIsAssigning] = useState(false);

  if (workloads.length === 0) {
    return null;
  }

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === PLACEHOLDER) return;
    const workloadId = value === '' ? null : value;

    setIsAssigning(true);
    try {
      await onAssign(workloadId);
      const workloadName = workloadId
        ? workloads.find(w => w.id === workloadId)?.name ?? 'workload'
        : 'Unassigned';
      toast.success(`Workload set to ${workloadName}`);
    } catch {
      toast.error('Failed to assign workload');
    } finally {
      setIsAssigning(false);
      // Reset bulk select back to placeholder after assignment
      if (bulk) {
        e.target.value = PLACEHOLDER;
      }
    }
  };

  const sizeClasses = size === 'sm'
    ? 'h-9 pl-3 pr-9 text-xs'
    : 'h-10 pl-3.5 pr-10 text-sm';

  const baseClasses = bulk
    ? 'border-purple-200 bg-purple-50 text-purple-800 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-200'
    : 'border-purple-200 bg-purple-100 text-purple-800 dark:border-purple-800 dark:bg-purple-900/30 dark:text-purple-100';

  const selectValue = bulk ? PLACEHOLDER : (currentWorkloadId ?? '');

  return (
    <div className="relative inline-flex shrink-0">
      <select
        value={selectValue}
        onChange={handleChange}
        disabled={disabled || isAssigning}
        className={`${sizeClasses} w-full appearance-none rounded-lg border font-medium shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed ${baseClasses}`}
      >
        {bulk && (
          <option value={PLACEHOLDER} disabled hidden>
            Workload
          </option>
        )}
        <option value="">
          {!bulk && !currentWorkloadId ? CHECK : ''}No Workload
        </option>
        {workloads.map((workload) => (
          <option key={workload.id} value={workload.id}>
            {!bulk && currentWorkloadId === workload.id ? CHECK : ''}{workload.name}
          </option>
        ))}
      </select>
      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-current opacity-70">
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </div>
    </div>
  );
};

export default WorkloadAssignmentSelect;
